/**
 * Catálogo psicomotor inicial — conteúdo PADRÃO do Gingado, carregado no boot
 * (todos os ambientes) pra a família não começar com a biblioteca vazia.
 *
 * Repertório enxuto, doméstico e seguro (PT-BR): jogos e materiais comuns de casa.
 * Cada item nasce sem mídia (emoji por nome/tipo) e com status "não usada" (honesto),
 * já com o perfil psicomotor preenchido (fator de Fonseca + sistema sensório-motor)
 * e um lembrete de segurança quando faz sentido. Id estável (`psm-<slug>`) pra ser
 * idempotente entre boots.
 */
import type { AtividadePsicomotora, PerfilPsicomotor } from "./types";

const PSICOMOTOR_VAZIO: PerfilPsicomotor = {
  fator_alvo: null,
  sistema: [],
  intensidade: null,
  previsibilidade: null,
};

type Semente = Pick<
  AtividadePsicomotora,
  "nome" | "tipo" | "demanda_relacional" | "seguranca"
> & { psicomotor?: Partial<PerfilPsicomotor> };

const SEMENTES: Semente[] = [
  {
    nome: "cama elástica",
    tipo: "material",
    demanda_relacional: "solo",
    seguranca: "supervisão constante; uma criança por vez; tirar sapatos.",
    psicomotor: {
      fator_alvo: "equilibracao",
      sistema: ["vestibular", "proprioceptivo", "motor_global"],
      intensidade: "ativadora",
      previsibilidade: "media",
    },
  },
  {
    nome: "túnel",
    tipo: "material",
    demanda_relacional: "parceiro",
    seguranca: "espaço livre nas pontas; sem empurrar.",
    psicomotor: {
      fator_alvo: "nocao_corpo",
      sistema: ["proprioceptivo", "tatil", "motor_global"],
      intensidade: "neutra",
      previsibilidade: "alta",
    },
  },
  {
    nome: "estátua / espelho",
    tipo: "jogo",
    demanda_relacional: "parceiro",
    seguranca: null,
    psicomotor: {
      fator_alvo: "praxia_global",
      sistema: ["motor_global", "proprioceptivo"],
      intensidade: "neutra",
      previsibilidade: "media",
    },
  },
  {
    nome: "circuito do tapete",
    tipo: "circuito",
    demanda_relacional: "parceiro",
    seguranca: "piso antiderrapante; afastar quinas e móveis.",
    psicomotor: {
      fator_alvo: "espaco_tempo",
      sistema: ["motor_global", "vestibular"],
      intensidade: "ativadora",
      previsibilidade: "alta",
    },
  },
  {
    nome: "balanço",
    tipo: "material",
    demanda_relacional: "parceiro",
    seguranca: "balanço lento e previsível; observar sinais de sobrecarga.",
    psicomotor: {
      fator_alvo: "tonicidade",
      sistema: ["vestibular", "proprioceptivo"],
      intensidade: "calmante",
      previsibilidade: "alta",
    },
  },
  {
    nome: "bola grande",
    tipo: "material",
    demanda_relacional: "parceiro",
    seguranca: "segurar a criança sobre a bola; superfície macia ao redor.",
    psicomotor: {
      fator_alvo: "equilibracao",
      sistema: ["vestibular", "proprioceptivo", "motor_global"],
      intensidade: "neutra",
      previsibilidade: "media",
    },
  },
  {
    nome: "empurrar almofadas (trabalho pesado)",
    tipo: "exercicio",
    demanda_relacional: "solo",
    seguranca: null,
    psicomotor: {
      fator_alvo: "tonicidade",
      sistema: ["proprioceptivo", "motor_global"],
      intensidade: "calmante",
      previsibilidade: "alta",
    },
  },
  {
    nome: "dança de imitar",
    tipo: "brincadeira",
    demanda_relacional: "parceiro",
    seguranca: null,
    psicomotor: {
      fator_alvo: "praxia_global",
      sistema: ["motor_global", "vestibular"],
      intensidade: "ativadora",
      previsibilidade: "baixa",
    },
  },
  {
    nome: "esconde-esconde (revezar)",
    tipo: "jogo",
    demanda_relacional: "parceiro",
    seguranca: null,
    psicomotor: {
      fator_alvo: "espaco_tempo",
      sistema: ["motor_global"],
      intensidade: "neutra",
      previsibilidade: "media",
    },
  },
  {
    nome: "amarelinha",
    tipo: "circuito",
    demanda_relacional: "solo",
    seguranca: "piso plano e seco.",
    psicomotor: {
      fator_alvo: "lateralizacao",
      sistema: ["motor_global", "vestibular"],
      intensidade: "ativadora",
      previsibilidade: "alta",
    },
  },
  {
    nome: "massinha / amassar",
    tipo: "material",
    demanda_relacional: "solo",
    seguranca: null,
    psicomotor: {
      fator_alvo: "praxia_fina",
      sistema: ["tatil", "motor_fino", "proprioceptivo"],
      intensidade: "calmante",
      previsibilidade: "alta",
    },
  },
];

const slug = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export function catalogoCorporalInicial(): AtividadePsicomotora[] {
  const agora = new Date().toISOString();
  return SEMENTES.map((s) => ({
    id: `psm-${slug(s.nome)}`,
    nome: s.nome,
    tipo: s.tipo,
    midias: [],
    psicomotor: { ...PSICOMOTOR_VAZIO, ...s.psicomotor },
    demanda_relacional: s.demanda_relacional,
    status: "nao_usada" as const,
    seguranca: s.seguranca,
    observacoes: null,
    arquivado: false,
    criado_em: agora,
  }));
}
